import { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Delete, DescriptionOutlined, VisibilityOutlined } from "@mui/icons-material";

import { ActionButton, DeleteModal, FormDialog } from "../v3";
import { getDateAndTime } from "../../utils/formatters";

interface TaskProgressDocument {
  id: number | string;
  originalName: string;
  createdAt?: string;
  url?: string;
}

interface TaskProgressAttachmentsModalProps {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  documents: TaskProgressDocument[];
  onView: (document: TaskProgressDocument) => void;
  onDelete: (documentId: number | string) => Promise<void>;
}

export const TaskProgressAttachmentsModal = ({
  open,
  onClose,
  loading = false,
  documents,
  onView,
  onDelete,
}: TaskProgressAttachmentsModalProps) => {
  const [deleteId, setDeleteId] = useState<number | string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleteId === null || deleting) return;

    try {
      setDeleting(true);
      await onDelete(deleteId);
      setDeleteId(null);
      if (documents.length === 1) {
        onClose();
      }
    } catch (error) {
      console.error("Failed to delete attachment:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <FormDialog
        open={open && deleteId === null}
        onClose={() => {
          if (deleting) return;
          setDeleteId(null);
          onClose();
        }}
        title={`Documents (${documents.length})`}
        actions={<ActionButton buttonText="Close" variant="outlined" onButtonClick={onClose} />}
      >
        <Box sx={{ my: 2 }}>
          {loading ? (
            <Typography color="text.secondary" textAlign="center">
              Loading attachments...
            </Typography>
          ) : documents.length === 0 ? (
            <Typography color="text.secondary" textAlign="center">
              No attachments found
            </Typography>
          ) : (
            documents.map((doc) => (
              <Box
                key={doc.id}
                display="flex"
                alignItems="center"
                gap={2}
                py={1.5}
                borderBottom="1px solid"
                borderColor="divider"
              >
                <DescriptionOutlined color="action" />

                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                    {doc.originalName}
                  </Typography>

                  {doc.createdAt && (
                    <Typography variant="caption" color="text.secondary">
                      {getDateAndTime(doc.createdAt)}
                    </Typography>
                  )}
                </Box>

                <IconButton
                  size="small"
                  aria-label="View document"
                  onClick={() => onView(doc)}
                  sx={{
                    color: "#674BF5",
                    border: "1px solid #E5DBFF",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      backgroundColor: "#F5F1FF",
                    },
                  }}
                >
                  <VisibilityOutlined fontSize="small" />
                </IconButton>

                <IconButton
                  size="small"
                  aria-label="Delete document"
                  onClick={() => setDeleteId(doc.id)}
                  sx={{
                    color: "#674BF5",
                    border: "1px solid #E5DBFF",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      backgroundColor: "#F5F1FF",
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
      </FormDialog>

      <DeleteModal
        isOpen={deleteId !== null}
        onClose={() => {
          if (!deleting) setDeleteId(null);
        }}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this document?"
      />
    </>
  );
};
